import source from "./chapters.json";
import { canAccess } from "../providers/commons/canAccess";
import { baseRole } from "../sales/roleProfiles";
import { isFullAccessRole } from "../sales/roles";
import { workspaceNavigation } from "../layout/workspaceNavigation";
type Chapter = (typeof source)[number];
const requirements: Record<string, [string, string]> = {
  activity: ["activity_log", "list"],
  contacts: ["contacts", "list"],
  "new-contact": ["contacts", "create"],
  "contact-card": ["contacts", "show"],
  notes: ["contact_notes", "list"],
  companies: ["companies", "list"],
  tags: ["contacts", "list"],
  csv: ["import", "create"],
  "task-list": ["tasks", "list"],
  "new-task": ["tasks", "create"],
  "task-work": ["tasks", "show"],
  calendar: ["tasks", "list"],
  "calendar-year": ["tasks", "list"],
  comments: ["tasks", "show"],
  expenses: ["tasks", "show"],
  "work-projects": ["work_projects", "list"],
  "deal-card": ["deals", "show"],
  "admin-tools": ["admin_tools", "list"],
};
export function visibleHelpChapters(role: string): Chapter[] {
  const full = isFullAccessRole(role);
  const allowed = (resource: string, action = "list") =>
    canAccess(role, { resource, action });
  return source
    .filter(
      (c) =>
        c.id !== "roles-workspaces" &&
        (requirements[c.id]
          ? allowed(...requirements[c.id])
          : [
              "start",
              "navigation",
              "preferences",
              "common-actions",
              "troubleshooting",
              "push",
            ].includes(c.id)),
    )
    .map((original) => {
      const c: Chapter = {
        ...original,
        desktop: [...original.desktop],
        mobile: [...original.mobile],
        controls: original.controls.map((x) => ({ ...x })),
      };
      if (c.id === "navigation") {
        const names = workspaceNavigation(role)
          .map((x) => (x.key === "projects" ? "Проекты" : x.label))
          .join(", ");
        c.summary =
          "Используйте меню для перехода между вашими рабочими разделами.";
        c.desktop = [
          `В меню доступны: ${names}.`,
          "Наведите указатель на пункт со стрелкой и выберите нужный подраздел.",
          "Нажмите аватарку справа, чтобы открыть кабинет, профиль и эту инструкцию.",
        ];
        c.mobile = [
          `В плавающем меню внизу доступны: ${names.replace("Контрагенты", "Контакты")}.`,
          "Нажмите нужный раздел. Если есть подразделы, выберите их в верхнем переключателе.",
          "Личные настройки и инструкция находятся в «Кабинете».",
        ];
        c.image = null;
        c.extra = null;
      }
      if (full) return c;
      // Existing illustrations were captured in a full-access workspace and show unrelated navigation.
      c.image = null;
      c.extra = null;
      if (c.id === "start") {
        c.summary =
          "Входите под своей учётной записью: поручения, комментарии и уведомления будут связаны с вами.";
        c.desktop[c.desktop.length - 1] =
          "После входа нажмите аватарку справа и откройте «Мой профиль». Проверьте имя, контактные данные и уведомления.";
        c.mobile = c.mobile.map((s) =>
          s.replace(
            "Назначенные поручения находятся во вкладке «Календарь».",
            allowed("tasks")
              ? "Назначенные поручения находятся во вкладке «Календарь»."
              : "",
          ),
        );
      }
      if (c.id === "work-projects") {
        c.title = "Проекты и контрольные точки";
        c.summary =
          "Откройте назначенный проект, чтобы посмотреть сроки, описание, документы и текущий этап работы.";
        c.desktop = [
          "Откройте «Мои проекты» и выберите назначенный вам проект.",
          "В дорожной карте выберите текущую контрольную точку. Следующие точки можно просматривать, но выполнять их заранее нельзя.",
          "Отметьте проверенные пункты, опишите ход работы, приложите фотографии или документы и сохраните отчёт. Незавершённый отчёт можно сохранить без отправки.",
          "После завершения всех пунктов сохраните итоговый отчёт и отправьте точку на проверку. Дождитесь результата проверки, прежде чем приступать к следующей точке.",
          "Если работу вернули, прочитайте причину и выполните исправления. Предыдущие попытки и пояснения находятся в «Журнале».",
          "В «Файлах» находятся документы для работы над проектом.",
        ];
        c.mobile = [
          "Нажмите «Проекты» в нижнем меню и выберите проект.",
          ...c.desktop.slice(1),
        ];
        c.controls = [
          {
            name: "Сохранить отчёт",
            action: "Сохраняет чек-лист, пояснение и вложения.",
          },
          {
            name: "Отправка на проверку",
            action: "Передаёт сохранённый итоговый отчёт на проверку.",
          },
          { name: "Файлы", action: "Документы для выполнения работ." },
          {
            name: "Журнал",
            action: "История отчётов, замечаний и предыдущих попыток.",
          },
        ];
        c.tip =
          "Проверяйте замечания и сохраняйте отчёт перед переходом к другой странице.";
      }
      if (c.id === "calendar" || c.id === "calendar-year") {
        c.summary =
          "Планируйте работу по датам и открывайте день, чтобы посмотреть его события.";
        c.desktop = [
          "Откройте «Календарь». Выберите масштаб «Год», «Месяц» или «День».",
          "Выберите дату, чтобы открыть события дня. Нажмите событие для просмотра подробностей.",
          "Используйте стрелки для перехода между периодами и «Сегодня» для возврата к текущей дате.",
          "В меню календаря можно выбрать отображаемые события и включить выполненные задачи.",
        ];
        if (["logistics", "lead_installer"].includes(baseRole(role))) {
          c.desktop.push(
            "Задачи и монтажи находятся в отдельных сворачиваемых группах. Сверните ненужную группу, чтобы освободить место.",
          );
        }
        c.mobile = [
          "Листайте календарь вверх и вниз: годы, месяцы и дни продолжаются непрерывно.",
          "Разведите два пальца, чтобы приблизить год до месяца, а месяц до дня. Сведите пальцы для обратного перехода. Один жест меняет масштаб на один уровень.",
          "Месяц и день также можно открыть нажатием. Кнопка назад возвращает к предыдущему масштабу, «Сегодня» — к текущей дате.",
          "Настройки доступны во всех масштабах. В меню дня задачи и монтажи можно сворачивать отдельно.",
        ];
        if (c.id === "calendar")
          c.mobile.unshift(
            "В блоке «Сегодня» на главной монтажи и задачи можно сворачивать отдельно. Полный список событий открывается по ссылке в календарь.",
          );
        c.controls = [
          { name: "Сегодня", action: "Возвращает к текущей дате." },
          { name: "Год / Месяц / День", action: "Меняет масштаб календаря." },
          {
            name: "Событие",
            action: "Открывает подробности и доступные действия.",
          },
        ];
        c.tip =
          "В миниатюре дня одна точка означает, что в этот день есть задачи.";
      }
      // A read-only workspace must not teach mutation controls that are absent there.
      const resource = ["contacts", "contact-card", "tags"].includes(c.id)
        ? "contacts"
        : c.id === "companies"
          ? "companies"
          : c.id === "notes"
            ? "contact_notes"
            : null;
      if (resource && !allowed(resource, "edit")) {
        c.desktop = [
          "Откройте нужную запись в списке, чтобы посмотреть подробности.",
          "Для поиска используйте строку с лупой. Фильтры уточняют выборку, сортировка меняет порядок записей.",
          "Для возврата используйте стрелку назад.",
        ];
        c.mobile = [...c.desktop];
        c.controls = [
          { name: "Поиск", action: "Находит нужные записи." },
          { name: "Фильтры", action: "Уточняют список отображаемых записей." },
          { name: "Назад", action: "Возвращает к списку." },
        ];
        c.tip = "В этой рабочей области записи доступны для просмотра.";
      }
      c.title = c.title.replace(
        "Теги и роли: как выбирать без путаницы",
        "Теги: как выбирать отметки",
      );
      const clean = (text: string) =>
        text
          .replace(/существующие роли или теги/g, "существующие теги")
          .replace(/существующие роли и теги/g, "существующие теги")
          .replace(/компании, роли, телефону/g, "компании, тегам, телефону")
          .replace(/если он доступен вашей роли/g, "в меню")
          .replace(
            /автору или администратору в пределах его прав/g,
            "в пределах доступных действий",
          );
      const internal =
        /(администр|админ|руководител|менеджер|бухгалтер|логист|сделк|финанс|аналитик|«управление»|(?:^|[^а-я])кп(?:[^а-я]|$)|(?:^|[^а-я])рол(?:ь|и|ей)(?:[^а-я]|$))/i;
      c.summary = clean(c.summary);
      if (internal.test(c.summary))
        c.summary =
          "Инструкция по работе с доступными вам действиями в этом разделе.";
      c.desktop = c.desktop.map(clean).filter((s) => !internal.test(s));
      c.mobile = c.mobile.map(clean).filter((s) => !internal.test(s));
      c.controls = c.controls
        .map((x) => ({ name: clean(x.name), action: clean(x.action) }))
        .filter((x) => !internal.test(x.name + " " + x.action));
      if (c.tip) {
        c.tip = clean(c.tip);
        if (internal.test(c.tip))
          c.tip = "Сохраняйте изменения перед переходом к другой странице.";
      }
      return c;
    });
}
